import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { Nis2AcnList, Nis2ExposedAddress, Nis2ExposedPort, Nis2Exposure } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, formatDateTime, Spinner, StatCard } from '../common'
import { DataTable, type Column } from '../DataTable'

/**
 * The attack surface of the organisation, as the NIS2 programme sees it: the
 * addresses reachable from the internet, the services on them, the systems
 * behind, the risk and why (see nis2_esposizione_service on the server).
 *
 * Where an address comes from is said on every row, because each source has
 * its own blind spot: a public IP declared on a CI or found on an interface,
 * a cloud instance, the flows from the internet (only when the traffic filter
 * keeps them) and, when switched on, Shodan's InternetDB. The same data feeds
 * the list for the yearly ACN update (public address space and domains).
 */

type Rischio = Nis2ExposedAddress['risk']

/** Higher = worse: the table opens with the riskiest addresses on top. */
const PESO_RISCHIO: Record<Rischio, number> = { alto: 2, medio: 1, basso: 0 }

const TITOLO: CSSProperties = { margin: '0 0 6px' }
const ELENCO: CSSProperties = { margin: 0, paddingLeft: 18 }

/** Literal t() calls: the translation guard does not see t(variable). */
function etichettaRischio(r: string): string {
  switch (r) {
    case 'alto': return t('alto')
    case 'medio': return t('medio')
    case 'basso': return t('basso')
    default: return r
  }
}

function classeRischio(r: string): string {
  return r === 'alto' ? 'badge badge--error' : r === 'medio' ? 'badge badge--warn' : 'badge badge--ok'
}

function etichettaOrigine(o: string): string {
  switch (o) {
    case 'pubblico': return t('IP pubblico')
    case 'cloud': return t('cloud')
    case 'traffico': return t('traffico')
    case 'internet': return t('InternetDB')
    default: return o
  }
}

function spiegaOrigine(o: string): string | undefined {
  switch (o) {
    case 'pubblico': return t('Indirizzo pubblico scritto su un CI (NAT, VIP) o rilevato sull’interfaccia di un dispositivo')
    case 'cloud': return t('Indirizzo pubblico di un’istanza cloud')
    case 'traffico': return t('Ha ricevuto connessioni da indirizzi Internet nei flussi')
    case 'internet': return t('Porta aperta secondo InternetDB (dati Shodan)')
    default: return undefined
  }
}

function etichettaReputazione(r: string | null): string {
  switch (r) {
    case 'segnalato': return t('in lista di abuso')
    case 'pulito': return t('pulito')
    case 'non_verificabile': return t('non verificabile')
    case null: return ''
    default: return r
  }
}

function classeReputazione(r: string | null): string {
  return r === 'segnalato' ? 'badge badge--error' : r === 'pulito' ? 'badge badge--ok' : 'badge badge--muted'
}

function protocollo(p: number | null): string {
  if (p === null || p === undefined) return ''
  return p === 6 ? 'tcp' : p === 17 ? 'udp' : p === 1 ? 'icmp' : String(p)
}

/** «3389/tcp RDP»: the port as text, for the cell, the search and the CSV. */
function nomePorta(p: Nis2ExposedPort): string {
  const proto = protocollo(p.protocol)
  return `${p.port}${proto ? `/${proto}` : ''}${p.service ? ` ${p.service}` : ''}`
}

function daSorgenti(n: number): string {
  return n === 1 ? t('da 1 sorgente') : t('da {n} sorgenti', { n })
}

function nomeCi(c: Nis2ExposedAddress['cis'][number]): string {
  return c.name || c.id.slice(0, 8)
}

function cveInternet(a: Nis2ExposedAddress): number {
  return a.internet?.vulns.length ?? 0
}

function Origini({ origini }: { origini: string[] }) {
  if (origini.length === 0) return <span className="text-muted">—</span>
  return (
    <>
      {origini.map((o) => (
        <span key={o} className="chip" title={spiegaOrigine(o)}>{etichettaOrigine(o)}</span>
      ))}
    </>
  )
}

/** A port: risky services as a coloured badge, the others as plain text. */
function Porta({ p }: { p: Nis2ExposedPort }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {p.service ? (
        <span className={classeRischio(p.risk)}
              title={t('Servizio a rischio {r} se raggiungibile da Internet', { r: etichettaRischio(p.risk) })}>
          <span className="mono">{nomePorta(p)}</span>
        </span>
      ) : (
        <span className="mono">{nomePorta(p)}</span>
      )}
      {p.sources > 0 && <small className="text-muted"> · {daSorgenti(p.sources)}</small>}
    </span>
  )
}

/** Ports shown in the cell before «+N»: the rest is in the detail panel. */
const PORTE_IN_CELLA = 6

function Porte({ a }: { a: Nis2ExposedAddress }) {
  if (a.ports.length === 0) return <span className="text-muted">—</span>
  // The risky ones first: they are the reason the row is there.
  const ordinate = [...a.ports].sort((x, y) =>
    (y.service ? PESO_RISCHIO[y.risk] + 1 : 0) - (x.service ? PESO_RISCHIO[x.risk] + 1 : 0) || x.port - y.port)
  const mostrate = ordinate.slice(0, PORTE_IN_CELLA)
  return (
    <>
      {mostrate.map((p) => <div key={p.port}><Porta p={p} /></div>)}
      {ordinate.length > mostrate.length && (
        <small className="text-muted">{t('+{n} altre', { n: ordinate.length - mostrate.length })}</small>
      )}
    </>
  )
}

function Sistemi({ a }: { a: Nis2ExposedAddress }) {
  if (a.cis.length === 0) return <span className="text-muted">—</span>
  return (
    <>
      {a.cis.map((c) => (
        <div key={c.id}>
          <Link to={`/cis/${c.id}`}>{nomeCi(c)}</Link>
          {!c.in_perimeter && (
            <>
              {' '}
              <small className="badge badge--warn" title={t('Il sistema non è nel perimetro NIS2')}>
                {t('fuori perimetro')}
              </small>
            </>
          )}
        </div>
      ))}
    </>
  )
}

function Vulnerabilita({ a }: { a: Nis2ExposedAddress }) {
  const cve = cveInternet(a)
  if (!a.exploited && !a.critical && !cve) return <span className="text-muted">—</span>
  return (
    <>
      {a.exploited > 0 && (
        <div>
          <span className="badge badge--error" title={t('Vulnerabilità sfruttate attivamente sui sistemi dietro l’indirizzo')}>
            {t('{n} sfruttate', { n: a.exploited })}
          </span>
        </div>
      )}
      {a.critical > 0 && (
        <div>
          <span className="badge badge--warn" title={t('Vulnerabilità critiche sui sistemi dietro l’indirizzo')}>
            {t('{n} critiche', { n: a.critical })}
          </span>
        </div>
      )}
      {cve > 0 && (
        <div>
          <span className="badge badge--error" title={t('CVE che InternetDB associa all’indirizzo')}>
            {t('{n} CVE da InternetDB', { n: cve })}
          </span>
        </div>
      )}
    </>
  )
}

function colonne(): Column<Nis2ExposedAddress>[] {
  return [
    {
      key: 'address',
      header: t('Indirizzo'),
      mono: true,
      value: (a) => a.address,
      render: (a) => (
        <>
          <span className="mono">{a.address}</span>
          {!a.public && <><br /><small className="text-muted">{t('privato, raggiunto da Internet')}</small></>}
        </>
      ),
    },
    {
      key: 'risk',
      header: t('Rischio'),
      // The label filters and goes in the CSV; the sort is by weight, so
      // «alto» comes first on the first click (numbers sort largest first).
      type: 'number',
      align: 'left',
      sortValue: (a) => ({ alto: 2, medio: 1, basso: 0 })[a.risk] ?? 0,
      value: (a) => etichettaRischio(a.risk),
      render: (a) => (
        <span className={classeRischio(a.risk)} style={{ cursor: a.reasons.length ? 'help' : undefined }}
              title={a.reasons.join('\n') || undefined}>
          {etichettaRischio(a.risk)}
        </span>
      ),
    },
    {
      key: 'origins',
      header: t('Origine'),
      value: (a) => a.origins.map(etichettaOrigine).join(', '),
      render: (a) => <Origini origini={a.origins} />,
    },
    {
      key: 'ports',
      header: t('Porte'),
      value: (a) => a.ports.map(nomePorta).join(', '),
      render: (a) => <Porte a={a} />,
    },
    {
      key: 'cis',
      header: t('Sistemi dietro'),
      value: (a) => a.cis.map((c) => `${nomeCi(c)}${c.in_perimeter ? '' : ` (${t('fuori perimetro')})`}`).join(', '),
      render: (a) => <Sistemi a={a} />,
    },
    {
      key: 'reputation',
      header: t('Reputazione'),
      value: (a) => etichettaReputazione(a.reputation),
      render: (a) =>
        a.reputation ? (
          <span className={classeReputazione(a.reputation)} title={a.reputation_detail || undefined}>
            {etichettaReputazione(a.reputation)}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'certificates',
      header: t('Certificati'),
      type: 'number',
      value: (a) => a.certificate_issues.length || null,
      render: (a) =>
        a.certificate_issues.length ? (
          <span className="badge badge--warn" style={{ cursor: 'help' }} title={a.certificate_issues.join('\n')}>
            {a.certificate_issues.length === 1
              ? t('1 problema')
              : t('{n} problemi', { n: a.certificate_issues.length })}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'vulns',
      header: t('Vulnerabilità'),
      type: 'number',
      value: (a) => a.exploited + a.critical + cveInternet(a) || null,
      render: (a) => <Vulnerabilita a={a} />,
    },
    {
      key: 'last_seen',
      header: t('Ultimo visto'),
      type: 'date',
      value: (a) => a.last_seen,
      render: (a) => (a.last_seen ? formatDateTime(a.last_seen) : '—'),
    },
  ]
}

/** Everything known about one address, under its row. */
function Dettaglio({ a, internetAttivo }: { a: Nis2ExposedAddress; internetAttivo: boolean }) {
  const i = a.internet
  const griglia: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }
  return (
    <div style={{ display: 'grid', gap: 20, padding: '8px 4px' }}>
      <div style={griglia}>
        <section>
          <h4 style={TITOLO}>{t('Perché questo livello di rischio')}</h4>
          {a.reasons.length ? (
            <ul style={ELENCO}>{a.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
          ) : (
            <p className="text-muted" style={{ margin: 0 }}>
              <small>{t('Nessun servizio rischioso, vulnerabilità o segnalazione nota.')}</small>
            </p>
          )}
        </section>
        <section>
          <h4 style={TITOLO}>{t('Sistemi dietro l’indirizzo')}</h4>
          {a.cis.length ? (
            <ul style={ELENCO}>
              {a.cis.map((c) => (
                <li key={c.id}>
                  <Link to={`/cis/${c.id}`}>{nomeCi(c)}</Link>{' '}
                  <small className="text-muted mono">{c.ci_type}</small>{' '}
                  {c.in_perimeter ? (
                    <small className="badge badge--ok">{t('nel perimetro')}</small>
                  ) : (
                    <small className="badge badge--warn">{t('fuori perimetro')}</small>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted" style={{ margin: 0 }}>
              <small>{t('Nessun sistema collegato: dichiara l’IP pubblico nella scheda del CI che lo espone (NAT, VIP).')}</small>
            </p>
          )}
          {a.ip_ci_id && (
            <p style={{ margin: '8px 0 0' }}>
              <Link to={`/cis/${a.ip_ci_id}`}>{t('Apri la scheda dell’IP pubblico')}</Link>
            </p>
          )}
        </section>
      </div>

      <section>
        <h4 style={TITOLO}>{t('Porte')}</h4>
        {a.ports.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>
            <small>{t('Nessuna porta nota: né i flussi da Internet né InternetDB ne indicano.')}</small>
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('Porta')}</th>
                  <th>{t('Servizio')}</th>
                  <th>{t('Rischio')}</th>
                  <th>{t('Origine')}</th>
                  <th>{t('Sorgenti Internet')}</th>
                  <th>{t('Ultimo visto')}</th>
                </tr>
              </thead>
              <tbody>
                {a.ports.map((p) => (
                  <tr key={p.port}>
                    <td className="mono">{p.port}{protocollo(p.protocol) ? `/${protocollo(p.protocol)}` : ''}</td>
                    <td>{p.service || <span className="text-muted">—</span>}</td>
                    <td><span className={classeRischio(p.risk)}>{etichettaRischio(p.risk)}</span></td>
                    <td><Origini origini={p.origins} /></td>
                    <td>{p.sources > 0 ? p.sources : <span className="text-muted">—</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{p.last_seen ? formatDateTime(p.last_seen) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div style={griglia}>
        <section>
          <h4 style={TITOLO}>{t('InternetDB')}</h4>
          {i ? (
            <dl className="kv">
              <dt>{t('Porte aperte')}</dt>
              <dd className="mono">{i.ports.length ? i.ports.join(', ') : '—'}</dd>
              <dt>{t('CVE note')}</dt>
              <dd>
                {i.vulns.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', maxHeight: 160, overflowY: 'auto' }}>
                    {i.vulns.map((cve) => (
                      <a key={cve} href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(cve)}`}
                         target="_blank" rel="noreferrer noopener" className="mono">
                        {cve}
                      </a>
                    ))}
                  </div>
                ) : '—'}
              </dd>
              <dt>{t('Nomi host')}</dt>
              <dd className="mono">{i.hostnames.length ? i.hostnames.join(', ') : '—'}</dd>
              <dt>{t('Tag')}</dt>
              <dd>{i.tags.length ? i.tags.map((x) => <span key={x} className="chip">{x}</span>) : '—'}</dd>
              <dt>{t('Verificato il')}</dt>
              <dd>{formatDateTime(i.checked_at)}</dd>
            </dl>
          ) : (
            <p className="text-muted" style={{ margin: 0 }}>
              <small>
                {!a.public
                  ? t('Indirizzo privato: InternetDB conosce solo gli indirizzi pubblici.')
                  : !internetAttivo
                    ? t('InternetDB non è attivo: si accende nel profilo NIS2.')
                    : a.ip_ci_id
                      ? t('Non ancora verificato: l’aggiornamento è giornaliero, oppure subito con «Aggiorna da InternetDB».')
                      : t('Si verificano su InternetDB gli IP pubblici registrati come CI: questo indirizzo non lo è ancora.')}
              </small>
            </p>
          )}
        </section>
        <section>
          <h4 style={TITOLO}>{t('Certificati')}</h4>
          {a.certificate_issues.length ? (
            <ul style={ELENCO}>{a.certificate_issues.map((c) => <li key={c}>{c}</li>)}</ul>
          ) : (
            <p className="text-muted" style={{ margin: 0 }}>
              <small>{t('Nessun problema noto sui certificati dei sistemi esposti.')}</small>
            </p>
          )}
          <h4 style={{ ...TITOLO, marginTop: 16 }}>{t('Reputazione')}</h4>
          {a.reputation ? (
            <p style={{ margin: 0 }}>
              <span className={classeReputazione(a.reputation)}>{etichettaReputazione(a.reputation)}</span>
              {a.reputation_detail && <small className="text-muted"> · {a.reputation_detail}</small>}
            </p>
          ) : (
            <p className="text-muted" style={{ margin: 0 }}><small>{t('Non ancora verificata.')}</small></p>
          )}
        </section>
      </div>
    </div>
  )
}

/** The plain-text list for the ACN form: one section per kind. */
function testoAcn(e: Nis2AcnList): string {
  const sezione = (titolo: string, voci: string[]) =>
    [`# ${titolo} (${voci.length})`, ...(voci.length ? voci : [t('(nessuno)')])].join('\n')
  return [
    sezione(t('Indirizzi IP pubblici'), e.addresses),
    sezione(t('Sottoreti pubbliche'), e.subnets),
    sezione(t('Nomi di dominio'), e.domains),
  ].join('\n\n')
}

/** The public address space and domains for the yearly ACN update. */
function DaComunicareAcn() {
  const [elenco, setElenco] = useState<Nis2AcnList | null>(null)
  const [carica, setCarica] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [copiato, setCopiato] = useState(false)

  async function prepara() {
    setCarica(true)
    setErrore(null)
    try {
      setElenco(await api.nis2AcnList())
    } catch (err) {
      setErrore(err)
    } finally {
      setCarica(false)
    }
  }

  const testo = elenco ? testoAcn(elenco) : ''

  async function copia() {
    // Outside a secure context there is no clipboard: the text stays
    // selectable in the box, which is enough.
    try {
      await navigator.clipboard.writeText(testo)
      setCopiato(true)
      setTimeout(() => setCopiato(false), 2000)
    } catch {
      setCopiato(false)
    }
  }

  return (
    <div style={{ marginTop: 28 }}>
      <h4 style={TITOLO}>{t('Da comunicare ad ACN')}</h4>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Nell’aggiornamento annuale delle informazioni (dal 15 aprile al 31 maggio) ACN chiede lo spazio di indirizzamento IP pubblico e i nomi di dominio del soggetto.')}
      </p>
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <button type="button" className="secondary" onClick={() => void prepara()} disabled={carica}>
          {elenco ? t('Ricalcola l’elenco') : t('Prepara l’elenco')}
        </button>
        {elenco && (
          <button type="button" className="secondary" onClick={() => void copia()}>
            {copiato ? t('Copiato') : t('Copia')}
          </button>
        )}
      </div>
      <ErrorBanner error={errore} />
      {carica && <Spinner />}
      {elenco && !carica && (
        <>
          <textarea
            readOnly
            className="mono"
            value={testo}
            rows={Math.min(24, testo.split('\n').length + 1)}
            style={{ width: '100%', boxSizing: 'border-box' }}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={t('Da comunicare ad ACN')}
          />
          <p className="field__hint">
            {t('È ciò che CENSO conosce: confrontalo con le assegnazioni del provider prima di comunicarlo.')}
            {elenco.domains.length === 0 && (
              <> {t('Nessun nome di dominio noto: i nomi arrivano dagli host noti a InternetDB, se attivo. Aggiungi a mano i domini registrati dal soggetto.')}</>
            )}
          </p>
        </>
      )}
    </div>
  )
}

type Filtro = 'alto' | 'servizi' | 'fuori' | 'abuso'

const FILTRI: Record<Filtro, { etichetta: () => string; vale: (a: Nis2ExposedAddress) => boolean }> = {
  alto: { etichetta: () => t('indirizzi ad alto rischio'), vale: (a) => a.risk === 'alto' },
  servizi: { etichetta: () => t('indirizzi con servizi rischiosi'), vale: (a) => a.ports.some((p) => p.risk === 'alto') },
  fuori: { etichetta: () => t('indirizzi con sistemi fuori perimetro'), vale: (a) => a.cis.length > 0 && !a.in_perimeter },
  abuso: { etichetta: () => t('indirizzi in liste di abuso'), vale: (a) => a.reputation === 'segnalato' },
}

export function SuperficieEsposta({ scrive }: { scrive: boolean }) {
  const esposizione = useAsync(() => api.nis2Exposure(), [])
  // The refresh answers with the whole report: it replaces the loaded one.
  const [aggiornata, setAggiornata] = useState<Nis2Exposure | null>(null)
  const [aggiorna, setAggiorna] = useState<{ busy: boolean; error: unknown; esito: Nis2Exposure['refresh'] | null }>(
    { busy: false, error: null, esito: null },
  )
  const [filtro, setFiltro] = useState<Filtro | null>(null)

  const dati = aggiornata ?? esposizione.data

  async function daInternetDb() {
    setAggiorna({ busy: true, error: null, esito: null })
    try {
      const r = await api.nis2ExposureRefresh()
      setAggiornata(r)
      setAggiorna({ busy: false, error: null, esito: r.refresh ?? null })
    } catch (err) {
      setAggiorna({ busy: false, error: err, esito: null })
    }
  }

  const internetAttivo = Boolean(dati?.internet_lookup.enabled)
  const azioni = internetAttivo && scrive ? (
    <button type="button" className="secondary" onClick={() => void daInternetDb()} disabled={aggiorna.busy}
            title={t('Chiede di nuovo a InternetDB ciò che sa degli indirizzi pubblici (nessuna scansione)')}>
      {aggiorna.busy ? t('Aggiornamento…') : t('Aggiorna da InternetDB')}
    </button>
  ) : undefined

  if (esposizione.loading && !dati) {
    return <Card title={t('Superficie esposta')}><Spinner /></Card>
  }
  if (!dati) {
    return <Card title={t('Superficie esposta')}><ErrorBanner error={esposizione.error} /></Card>
  }

  const s = dati.summary
  const tutte = [...dati.addresses].sort(
    (x, y) => PESO_RISCHIO[y.risk] - PESO_RISCHIO[x.risk] || x.address.localeCompare(y.address, undefined, { numeric: true }),
  )
  const righe = filtro ? tutte.filter(FILTRI[filtro].vale) : tutte
  const alterna = (f: Filtro) => setFiltro((attuale) => (attuale === f ? null : f))

  return (
    <Card title={t('Superficie esposta')} actions={azioni}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ciò che l’organizzazione espone a Internet: indirizzi, servizi raggiungibili e sistemi che ci stanno dietro, con il livello di rischio e il motivo. È la superficie d’attacco da conoscere e da dichiarare.')}
      </p>

      <div className="stats">
        <StatCard
          label={t('Indirizzi esposti')}
          value={s.addresses}
          hint={t('{n} pubblici', { n: s.public })}
          onClick={filtro ? () => setFiltro(null) : undefined}
        />
        <StatCard
          label={t('Ad alto rischio')}
          value={s.high}
          tone={s.high > 0 ? 'error' : 'ok'}
          hint={t('{n} a rischio medio', { n: s.medium })}
          onClick={s.high > 0 ? () => alterna('alto') : undefined}
        />
        <StatCard
          label={t('Servizi rischiosi raggiungibili')}
          value={s.risky_services}
          tone={s.risky_services > 0 ? 'error' : 'ok'}
          hint={t('amministrazione remota, condivisione file, database…')}
          onClick={s.risky_services > 0 ? () => alterna('servizi') : undefined}
        />
        <StatCard
          label={t('Esposti fuori perimetro')}
          value={s.outside_perimeter}
          tone={s.outside_perimeter > 0 ? 'warn' : undefined}
          hint={t('sistemi esposti non inclusi nel perimetro NIS2')}
          onClick={s.outside_perimeter > 0 ? () => alterna('fuori') : undefined}
        />
        <StatCard
          label={t('In liste di abuso')}
          value={s.flagged}
          tone={s.flagged > 0 ? 'error' : undefined}
          onClick={s.flagged > 0 ? () => alterna('abuso') : undefined}
        />
      </div>

      {dati.traffic.visible ? (
        <p className="field__hint">
          {t('Le porte raggiunte da Internet vengono dai flussi degli ultimi {d} giorni: {n} coppie indirizzo/porta con sorgenti esterne.', { d: dati.traffic.days, n: dati.traffic.rows })}
          {' '}{t('Calcolato il {d}.', { d: formatDateTime(dati.generated_at) })}
        </p>
      ) : (
        <div className="banner banner--warn">
          {t('Il filtro del traffico è «Entrambi interni»: i flussi da e verso Internet vengono scartati all’arrivo, quindi manca la parte «raggiunto da Internet». Per vederla: Impostazioni → Traffico da conservare → «Almeno uno interno» (lo sceglie un amministratore del tenant). Gli IP pubblici dichiarati, il cloud e InternetDB funzionano comunque.')}
          {' '}<Link to="/settings">{t('Apri le Impostazioni')}</Link>
        </div>
      )}

      {internetAttivo ? (
        <p className="field__hint">
          {t('InternetDB è attivo: per gli indirizzi pubblici CENSO legge ciò che Shodan sa (porte aperte, CVE note, nomi host), senza scansioni. Si aggiorna da solo una volta al giorno.')}
        </p>
      ) : (
        <p className="field__hint">
          {t('InternetDB non è attivo. Si accende nel profilo NIS2 (sezione «Profilo e perimetro»): è una lettura passiva dei dati di Shodan, senza alcuna scansione, e dalla piattaforma escono solo gli indirizzi pubblici dell’organizzazione.')}
        </p>
      )}
      <ErrorBanner error={aggiorna.error} />
      {aggiorna.esito && (
        aggiorna.esito.enabled ? (
          <div className={`banner ${aggiorna.esito.failed > 0 ? 'banner--warn' : 'banner--ok'}`}>
            {t('InternetDB: {n} indirizzi verificati, {m} non riusciti.', { n: aggiorna.esito.checked, m: aggiorna.esito.failed })}
          </div>
        ) : (
          <div className="banner banner--warn">{t('InternetDB non è attivo: si accende nel profilo NIS2.')}</div>
        )
      )}

      {tutte.length === 0 ? (
        <EmptyState
          title={t('Nessun indirizzo esposto conosciuto')}
          hint={t('Gli indirizzi esposti arrivano da: gli IP pubblici dichiarati nella scheda di un CI (NAT, VIP) o rilevati sulle interfacce dei dispositivi; la discovery cloud (IP pubblici delle istanze); il traffico ricevuto da Internet (serve il filtro «Almeno uno interno»); InternetDB, se attivo nel profilo NIS2.')}
        />
      ) : (
        <>
          {filtro && (
            <p className="field__hint">
              {t('Mostrati solo: {f}.', { f: FILTRI[filtro].etichetta() })}{' '}
              <button type="button" className="link" onClick={() => setFiltro(null)}>{t('Mostra tutti')}</button>
            </p>
          )}
          <DataTable
            id="nis2-superficie-esposta"
            rows={righe}
            columns={colonne()}
            rowKey={(a) => a.address}
            conteggio={{ nome: t('indirizzi') }}
            nomeEsportazione={t('Superficie esposta')}
            expand={(a) => <Dettaglio a={a} internetAttivo={internetAttivo} />}
          />
        </>
      )}

      <DaComunicareAcn />
    </Card>
  )
}
